// The stretches of an asset a viewer watched, in the shape the heartbeat's `watched` carries:
// seconds from the start of the asset, sorted, no two overlapping or touching, and never more
// than the server reads. Pure, so the merging and the cap are tested without a player.

import type { WatchedRange } from '@/api/visitor/playback'

/** The most stretches the server reads from one report; it ignores any past this many. */
export const MAX_WATCHED_RANGES = 256

/**
 * `ranges` with the stretch `[start, end]` added, as a new array — `ranges` is left as it was, so
 * a payload already built from it keeps what it held.
 *
 * The stretches the new one overlaps or touches are joined to it. Touching is an end equal to a
 * start: one tick's stretch begins at the exact time the previous tick's ended, so a run of ticks
 * is one stretch, and no tolerance is needed to make it so. An empty or inverted stretch, or one
 * with a bound that is not a number, changes nothing.
 *
 * Past `MAX_WATCHED_RANGES`, the two neighbours separated by the smallest gap are joined, the
 * earliest pair on a tie. Sending more would lose the stretches past the server's limit outright;
 * joining instead reports the gap as watched, and the smallest gap is the least that can be
 * overstated.
 */
export function addWatchedRange(ranges: readonly WatchedRange[], start: number, end: number): WatchedRange[] {
  if (!(end > start)) return ranges.slice()

  const result: WatchedRange[] = []
  let added: WatchedRange = { start, end }
  let placed = false
  for (const range of ranges) {
    if (range.end < added.start) {
      result.push(range)
    } else if (range.start > added.end) {
      if (!placed) {
        result.push(added)
        placed = true
      }
      result.push(range)
    } else {
      added = { start: Math.min(range.start, added.start), end: Math.max(range.end, added.end) }
    }
  }
  if (!placed) result.push(added)

  while (result.length > MAX_WATCHED_RANGES) joinClosestNeighbours(result)
  return result
}

/** Replaces the two neighbours of `ranges` separated by the smallest gap with one stretch over both. */
function joinClosestNeighbours(ranges: WatchedRange[]): void {
  let closest: { at: number; gap: number; joined: WatchedRange } | undefined
  let left: WatchedRange | undefined
  for (const [at, right] of ranges.entries()) {
    if (left) {
      const gap = right.start - left.end
      if (!closest || gap < closest.gap) closest = { at: at - 1, gap, joined: { start: left.start, end: right.end } }
    }
    left = right
  }
  if (closest) ranges.splice(closest.at, 2, closest.joined)
}
